import { useMemo } from 'react';
import { parseISO, getISOWeek, getYear } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
} from 'recharts';
import { DollarSign, Hash } from 'lucide-react';
import { cn } from '@/lib/utils';
import { loadPricing, findUnitCost } from '@/lib/pricing';
import type { ShiftLog } from '@/types/drilling';

const MM_TO_HAMMER_KEY: Record<number, string> = {
    89: '3.5" (89mm)',
    102: '4" (102mm)',
    127: '5" (127mm)',
    152: '6" (152mm)',
    178: '7" (178mm)',
    203: '8" (203mm)',
    254: '10" (254mm)',
    305: '12" (305mm)',
};

const tooltipStyle = {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
};

interface CpmAnalysisProps {
    logs: ShiftLog[];
}

export default function CpmAnalysis({ logs }: CpmAnalysisProps) {
    const pricing = useMemo(() => loadPricing(), [logs]);
    const hasPricing = pricing.length > 0;

    const cpmByRig = useMemo(() => {
        const map: Record<string, { cost: number; metres: number }> = {};
        for (const log of logs) {
            const rig = log.shiftDetails?.rigId;
            if (!rig) continue;
            if (!map[rig]) map[rig] = { cost: 0, metres: 0 };
            map[rig].metres += log.drillHoles?.reduce((s, h) => s + (h.depth ?? 0), 0) ?? 0;
            map[rig].cost +=
                log.consumables?.reduce((s, c) => s + findUnitCost(c, pricing), 0) ?? 0;
        }
        return Object.entries(map)
            .map(([rig, d]) => ({
                rig,
                cpm: d.metres > 0 ? Math.round((d.cost / d.metres) * 100) / 100 : 0,
            }))
            .sort((a, b) => b.cpm - a.cpm);
    }, [logs, pricing]);

    const cpmByDiameter = useMemo(() => {
        const map: Record<string, { cost: number; metres: number }> = {};
        for (const log of logs) {
            const diam = log.drillHoles?.[0]?.diameter;
            if (!diam) continue;
            const key = `${diam}mm`;
            if (!map[key]) map[key] = { cost: 0, metres: 0 };
            map[key].metres += log.drillHoles!.reduce((s, h) => s + (h.depth ?? 0), 0);
            map[key].cost +=
                log.consumables?.reduce((s, c) => s + findUnitCost(c, pricing), 0) ?? 0;
        }
        return Object.entries(map)
            .map(([diameter, d]) => ({
                diameter,
                cpm: d.metres > 0 ? Math.round((d.cost / d.metres) * 100) / 100 : 0,
                totalCost: d.cost,
                totalMetres: d.metres,
            }))
            .sort((a, b) => a.diameter.localeCompare(b.diameter));
    }, [logs, pricing]);

    const cpmByOperator = useMemo(() => {
        const map: Record<
            string,
            { cost: number; metres: number; fullName: string; shifts: number }
        > = {};
        for (const log of logs) {
            const name = log.shiftDetails?.operatorName;
            if (!name) continue;
            if (!map[name]) map[name] = { cost: 0, metres: 0, fullName: name, shifts: 0 };
            map[name].metres += log.drillHoles?.reduce((s, h) => s + (h.depth ?? 0), 0) ?? 0;
            map[name].cost +=
                log.consumables?.reduce((s, c) => s + findUnitCost(c, pricing), 0) ?? 0;
            map[name].shifts++;
        }
        return Object.entries(map)
            .map(([, d]) => ({
                name: d.fullName.split(' ')[0],
                fullName: d.fullName,
                shifts: d.shifts,
                totalMetres: Math.round(d.metres),
                avgMetresPerShift: d.shifts > 0 ? Math.round(d.metres / d.shifts) : 0,
                cpm: d.metres > 0 ? Math.round((d.cost / d.metres) * 100) / 100 : 0,
            }))
            .sort((a, b) => b.cpm - a.cpm);
    }, [logs, pricing]);

    // CPM trend by ISO week
    const cpmTrend = useMemo(() => {
        const map: Record<string, { cost: number; metres: number }> = {};
        for (const log of logs) {
            const date = log.shiftDetails?.date;
            if (!date) continue;
            const parsed = parseISO(date);
            const week = `${getYear(parsed)}-W${String(getISOWeek(parsed)).padStart(2, '0')}`;
            if (!map[week]) map[week] = { cost: 0, metres: 0 };
            map[week].metres += log.drillHoles?.reduce((s, h) => s + (h.depth ?? 0), 0) ?? 0;
            map[week].cost +=
                log.consumables?.reduce((s, c) => s + findUnitCost(c, pricing), 0) ?? 0;
        }
        return Object.entries(map)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([week, d]) => ({
                week,
                cpm: d.metres > 0 ? Math.round((d.cost / d.metres) * 100) / 100 : 0,
            }));
    }, [logs, pricing]);

    // Consumable cost breakdown
    const costBreakdown = useMemo(() => {
        const byType: Record<string, number> = {};
        for (const log of logs) {
            for (const c of log.consumables ?? []) {
                const cost = findUnitCost(c, pricing);
                if (cost > 0) byType[c.type] = (byType[c.type] ?? 0) + cost;
            }
        }
        const total = Object.values(byType).reduce((s, v) => s + v, 0);
        return {
            total,
            items: Object.entries(byType)
                .filter(([, cost]) => cost > 0)
                .map(([type, cost]) => ({
                    label: type.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                    cost: Math.round(cost),
                    pct: total > 0 ? Math.round((cost / total) * 100) : 0,
                }))
                .sort((a, b) => b.cost - a.cost),
        };
    }, [logs, pricing]);

    const consumableUsage = useMemo(() => {
        const bits: Record<string, number> = {};
        const hammers: Record<string, number> = {};
        const rods: Record<string, number> = {};
        for (const log of logs) {
            for (const c of log.consumables ?? []) {
                if (c.type === 'drill-bit') {
                    const key = c.bitDiameter
                        ? `${c.bitDiameter}mm${c.bitType ? ` ${c.bitType}` : ''}`
                        : c.name || 'Unknown';
                    bits[key] = (bits[key] ?? 0) + 1;
                } else if (c.type === 'hammer') {
                    const key = c.bitDiameter
                        ? (MM_TO_HAMMER_KEY[c.bitDiameter] ?? `${c.bitDiameter}mm`)
                        : c.name || 'Unknown';
                    hammers[key] = (hammers[key] ?? 0) + 1;
                } else if (c.type === 'drill-rod') {
                    const key = c.bitDiameter ? `${c.bitDiameter}mm` : c.name || 'Unknown';
                    rods[key] = (rods[key] ?? 0) + 1;
                }
            }
        }
        return {
            bits: Object.entries(bits).sort((a, b) => b[1] - a[1]),
            hammers: Object.entries(hammers).sort((a, b) => b[1] - a[1]),
            rods: Object.entries(rods).sort((a, b) => b[1] - a[1]),
        };
    }, [logs]);

    const hammerTiles = useMemo(() => {
        const usedKeys = new Set(consumableUsage.hammers.map(([key]) => key));
        const used = consumableUsage.hammers.map(([key, count]) => ({
            size: key,
            label: key.split(' ')[0],
            count: count as number | undefined,
        }));
        const placeholders = [
            ...new Set(
                pricing
                    .filter((p) => p.type === 'hammer' && !usedKeys.has(p.subType))
                    .map((p) => p.subType)
            ),
        ]
            .sort()
            .map((size) => ({
                size,
                label: size.split(' ')[0],
                count: undefined as number | undefined,
            }));
        return [...used, ...placeholders];
    }, [pricing, consumableUsage.hammers]);

    const rodTiles = useMemo(() => {
        const usedKeys = new Set(consumableUsage.rods.map(([key]) => key));
        const used = consumableUsage.rods.map(([key, count]) => ({
            size: key,
            label: key,
            count: count as number | undefined,
        }));
        const placeholders = [
            ...new Set(
                pricing
                    .filter((p) => p.type === 'drill-rod' && !usedKeys.has(p.diameter))
                    .map((p) => p.diameter)
            ),
        ]
            .sort()
            .map((size) => ({
                size,
                label: size,
                count: undefined as number | undefined,
            }));
        return [...used, ...placeholders];
    }, [pricing, consumableUsage.rods]);


    return (
        <div className='space-y-6 mb-6'>
            <div className='flex items-center justify-between'>
                <h3 className='text-base font-semibold text-foreground'>Cost Per Metre (CPM)</h3>
                {!hasPricing && (
                    <p className='text-sm text-muted-foreground'>
                        Add consumable pricing in <strong>Settings</strong> to see cost data.
                    </p>
                )}
            </div>

            {/* CPM by diameter — summary tiles */}
            <div className='grid grid-cols-4 gap-3'>
                {cpmByDiameter.map((d) => (
                    <Card key={d.diameter} className='bg-card border-border'>
                        <CardHeader className='pb-2'>
                            <CardTitle className='text-sm font-medium text-muted-foreground flex items-center gap-2'>
                                <DollarSign className='h-4 w-4' />
                                Avg CPM — {d.diameter}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className='text-2xl font-bold text-foreground'>
                                {d.cpm > 0 ? `$${d.cpm.toFixed(2)}/m` : '—'}
                            </p>
                            <p className='text-xs text-muted-foreground'>
                                ${Math.round(d.totalCost).toLocaleString()} total ·{' '}
                                {Math.round(d.totalMetres).toLocaleString()}m drilled
                            </p>
                        </CardContent>
                    </Card>
                ))}
                <Card className='bg-card border-border border-dashed opacity-40'>
                    <CardHeader className='pb-2'>
                        <CardTitle className='text-sm font-medium text-muted-foreground'>
                            —
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className='text-2xl font-bold text-foreground'>—</p>
                    </CardContent>
                </Card>
                <Card className='bg-card border-border border-dashed opacity-40'>
                    <CardHeader className='pb-2'>
                        <CardTitle className='text-sm font-medium text-muted-foreground'>
                            —
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className='text-2xl font-bold text-foreground'>—</p>
                    </CardContent>
                </Card>
                {cpmByDiameter.length === 0 && (
                    <p className='col-span-4 text-sm text-muted-foreground text-center py-4'>
                        No data
                    </p>
                )}
            </div>

            {/* Consumable usage tiles */}
            <div className='space-y-4'>
                <div>
                    <h4 className='text-sm font-medium text-muted-foreground mb-3'>
                        Drill Bits Used
                    </h4>
                    {consumableUsage.bits.length > 0 ? (
                        <div className='grid grid-cols-4 gap-3'>
                            {consumableUsage.bits.map(([size, count]) => (
                                <Card key={size} className='bg-card border-border'>
                                    <CardHeader className='pb-1 pt-3 px-4'>
                                        <CardTitle className='text-xs font-medium text-muted-foreground flex items-center gap-1.5'>
                                            <Hash className='h-3 w-3' />
                                            {size}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className='pb-3 px-4'>
                                        <p className='text-2xl font-bold text-foreground'>
                                            {count}
                                        </p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground py-2'>No bit data for the selected period.</p>
                    )}
                </div>

                <div>
                    <h4 className='text-sm font-medium text-muted-foreground mb-3'>
                        Hammers Used
                    </h4>
                    {hammerTiles.length > 0 ? (
                        <div className='grid grid-cols-4 gap-3'>
                            {hammerTiles.map(({ size, label, count }) => (
                                <Card
                                    key={size}
                                    className={cn(
                                        'bg-card border-border',
                                        !count && 'border-dashed opacity-40'
                                    )}
                                >
                                    <CardHeader className='pb-1 pt-3 px-4'>
                                        <CardTitle className='text-xs font-medium text-muted-foreground flex items-center gap-1.5'>
                                            <Hash className='h-3 w-3' />
                                            {label}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className='pb-3 px-4'>
                                        <p className='text-2xl font-bold text-foreground'>
                                            {count ?? '—'}
                                        </p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground py-2'>No hammer data for the selected period.</p>
                    )}
                </div>

                <div>
                    <h4 className='text-sm font-medium text-muted-foreground mb-3'>
                        Drill Rods Used
                    </h4>
                    {rodTiles.length > 0 ? (
                        <div className='grid grid-cols-4 gap-3'>
                            {rodTiles.map(({ size, label, count }) => (
                                <Card
                                    key={size}
                                    className={cn(
                                        'bg-card border-border',
                                        !count && 'border-dashed opacity-40'
                                    )}
                                >
                                    <CardHeader className='pb-1 pt-3 px-4'>
                                        <CardTitle className='text-xs font-medium text-muted-foreground flex items-center gap-1.5'>
                                            <Hash className='h-3 w-3' />
                                            {label}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className='pb-3 px-4'>
                                        <p className='text-2xl font-bold text-foreground'>
                                            {count ?? '—'}
                                        </p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground py-2'>No rod data for the selected period.</p>
                    )}
                </div>
            </div>

            {/* Consumable cost breakdown + CPM trend */}
            {hasPricing && costBreakdown.total > 0 && (
                <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                    {/* Cost breakdown tile */}
                    <Card className='bg-card border-border'>
                        <CardHeader>
                            <CardTitle className='text-foreground'>
                                Consumable Cost Breakdown
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className='text-2xl font-bold text-foreground mb-4'>
                                ${costBreakdown.total.toLocaleString()}
                            </p>
                            <div className='space-y-3'>
                                {costBreakdown.items.map(({ label, cost, pct }) => (
                                    <div key={label}>
                                        <div className='flex justify-between text-sm mb-1'>
                                            <span className='text-muted-foreground'>{label}</span>
                                            <span className='font-medium text-foreground'>
                                                {pct}% · ${cost.toLocaleString()}
                                            </span>
                                        </div>
                                        <div className='h-1.5 bg-muted rounded-full overflow-hidden'>
                                            <div
                                                className='h-full bg-primary rounded-full'
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* CPM trend chart */}
                    {cpmTrend.length > 1 && (
                        <Card className='bg-card border-border'>
                            <CardHeader>
                                <CardTitle className='text-foreground'>
                                    CPM Trend (by week)
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ResponsiveContainer width='100%' height={220}>
                                    <LineChart data={cpmTrend}>
                                        <CartesianGrid
                                            strokeDasharray='3 3'
                                            stroke='hsl(var(--border))'
                                        />
                                        <XAxis
                                            dataKey='week'
                                            stroke='hsl(var(--muted-foreground))'
                                            fontSize={11}
                                        />
                                        <YAxis
                                            stroke='hsl(var(--muted-foreground))'
                                            fontSize={12}
                                            tickFormatter={(v) => `$${v}`}
                                        />
                                        <Tooltip
                                            contentStyle={tooltipStyle}
                                            formatter={(v) => [`$${v}/m`, 'CPM']}
                                        />
                                        <Line
                                            type='monotone'
                                            dataKey='cpm'
                                            stroke='hsl(var(--chart-5))'
                                            strokeWidth={2}
                                            dot={{ r: 3, fill: 'hsl(var(--chart-5))' }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* CPM by Drill Asset */}
            <Card className='bg-card border-border'>
                <CardHeader>
                    <CardTitle className='text-foreground'>CPM by Drill Asset</CardTitle>
                </CardHeader>
                <CardContent>
                    {cpmByRig.length === 0 ? (
                        <p className='text-sm text-muted-foreground text-center py-8'>
                            No rig data
                        </p>
                    ) : (
                        <ResponsiveContainer
                            width='100%'
                            height={Math.max(240, cpmByRig.length * 36)}
                        >
                            <BarChart
                                data={cpmByRig}
                                layout='vertical'
                                margin={{ left: 10, right: 30 }}
                            >
                                <CartesianGrid strokeDasharray='3 3' stroke='hsl(var(--border))' />
                                <XAxis
                                    type='number'
                                    stroke='hsl(var(--muted-foreground))'
                                    fontSize={12}
                                    tickFormatter={(v) => `$${v}`}
                                />
                                <YAxis
                                    dataKey='rig'
                                    type='category'
                                    stroke='hsl(var(--muted-foreground))'
                                    fontSize={11}
                                    width={80}
                                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                                />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    formatter={(value) => [`$${value}/m`, 'CPM']}
                                />
                                <Bar
                                    dataKey='cpm'
                                    fill='hsl(var(--chart-3))'
                                    radius={[0, 4, 4, 0]}
                                    barSize={14}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>

            {/* CPM by Operator */}
            <Card className='bg-card border-border'>
                <CardHeader>
                    <CardTitle className='text-foreground'>CPM by Operator</CardTitle>
                </CardHeader>
                <CardContent>
                    {cpmByOperator.length === 0 ? (
                        <p className='text-sm text-muted-foreground text-center py-8'>
                            No operator data
                        </p>
                    ) : (
                        <ResponsiveContainer
                            width='100%'
                            height={Math.max(240, cpmByOperator.length * 36)}
                        >
                            <BarChart
                                data={cpmByOperator}
                                layout='vertical'
                                margin={{ left: 10, right: 30 }}
                            >
                                <CartesianGrid strokeDasharray='3 3' stroke='hsl(var(--border))' />
                                <XAxis
                                    type='number'
                                    stroke='hsl(var(--muted-foreground))'
                                    fontSize={12}
                                    tickFormatter={(v) => `$${v}`}
                                />
                                <YAxis
                                    dataKey='name'
                                    type='category'
                                    stroke='hsl(var(--muted-foreground))'
                                    fontSize={12}
                                    width={80}
                                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                                />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    formatter={(value) => [`$${value}/m`, 'CPM']}
                                    labelFormatter={(label) =>
                                        cpmByOperator.find((o) => o.name === label)?.fullName ??
                                        label
                                    }
                                />
                                <Bar
                                    dataKey='cpm'
                                    fill='hsl(var(--chart-4))'
                                    radius={[0, 4, 4, 0]}
                                    barSize={14}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>

            {/* Operator Ranking Table */}
            {cpmByOperator.length > 0 && (
                <Card className='bg-card border-border'>
                    <CardHeader>
                        <CardTitle className='text-foreground'>Operator Rankings</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-border text-left text-muted-foreground'>
                                        <th className='pb-2 font-medium w-8'>#</th>
                                        <th className='pb-2 font-medium'>Operator</th>
                                        <th className='pb-2 font-medium text-right'>Shifts</th>
                                        <th className='pb-2 font-medium text-right'>Total m</th>
                                        <th className='pb-2 font-medium text-right'>
                                            Avg m / Shift
                                        </th>
                                        <th className='pb-2 font-medium text-right'>CPM</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...cpmByOperator]
                                        .sort((a, b) => b.avgMetresPerShift - a.avgMetresPerShift)
                                        .map((op, i) => (
                                            <tr
                                                key={op.fullName}
                                                className='border-b border-border/50 last:border-0'
                                            >
                                                <td className='py-2 text-muted-foreground'>
                                                    {i + 1}
                                                </td>
                                                <td className='py-2 font-medium text-foreground'>
                                                    {op.fullName}
                                                </td>
                                                <td className='py-2 text-right text-muted-foreground'>
                                                    {op.shifts}
                                                </td>
                                                <td className='py-2 text-right text-foreground'>
                                                    {op.totalMetres.toLocaleString()}m
                                                </td>
                                                <td className='py-2 text-right text-foreground'>
                                                    {op.avgMetresPerShift.toLocaleString()}m
                                                </td>
                                                <td className='py-2 text-right'>
                                                    {op.cpm > 0 ? (
                                                        <span className='font-medium'>
                                                            ${op.cpm.toFixed(2)}/m
                                                        </span>
                                                    ) : (
                                                        <span className='text-muted-foreground'>
                                                            —
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
